import type { ChangeEvent } from "react";
import React from "react";
import { Box, Tooltip, IconButton, CircularProgress } from "@mui/joy";
import { SAddDocument, SDelete, SView } from "../../../styles/StyledMUI";
import { colorTokens } from "../../../styles/globalStyles";
import DarkTypography from "../DarkTypography";

interface DocumentTypeComponentProps {
  uploadFile: (event: any, type: string) => Promise<void>;
  deleteFile: (fileType: string) => Promise<void>;
  handleView: (type: string) => void;
  title: string;
  document: string | undefined;
  documentType: string;
}

function DocumentTypeComponent({
  uploadFile,
  deleteFile,
  handleView,
  title,
  document,
  documentType,
}: DocumentTypeComponentProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleUpload = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsLoading(true);
      uploadFile(e, documentType).then(() => setIsLoading(false));
    },
    [uploadFile, documentType],
  );

  return (
    <Box
      sx={{
        border: `1px solid ${colorTokens.border.default}`,
        borderRadius: "8px",
        padding: 2,
        textAlign: "center",
        width: "100%",
        backgroundColor: colorTokens.bg.surface,
      }}
    >
      <DarkTypography level="title-md" colorVariant="title" sx={{ mb: 1, display: "block" }}>
        {title}
      </DarkTypography>

      {!isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <label>
            <Box
              component="input"
              type="file"
              onChange={handleUpload}
              accept="application/pdf"
              sx={{ display: "none" }}
            />
            <Tooltip title="Ajouter document">
              <IconButton component="span" variant="plain" sx={{ color: colorTokens.text.primary }}>
                <SAddDocument sx={{ color: "inherit" }} />
              </IconButton>
            </Tooltip>
          </label>

          {document ? (
            <>
              <Tooltip title="Voir document">
                <IconButton
                  onClick={() => handleView(documentType)}
                  variant="plain"
                  sx={{ color: colorTokens.text.primary }}
                >
                  <SView sx={{ color: "inherit" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Supprimer document">
                <IconButton
                  onClick={() => deleteFile(documentType)}
                  variant="plain"
                  sx={{ color: colorTokens.status.danger }}
                >
                  <SDelete sx={{ color: "inherit" }} />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress size="sm" sx={{ color: colorTokens.accent.primary }} />
        </Box>
      )}
    </Box>
  );
}

export default React.memo(DocumentTypeComponent);
